
import React from 'react';
import { Polyline, Polygon, Popup } from 'react-leaflet';
import { Trash2, Edit3 } from 'lucide-react';
import { LineContent, AreaContent } from '../../types';
import { formatDistance, formatArea } from '../../services/myContentService';
import { useTranslation } from 'react-i18next';

interface DrawingLayerProps {
  lines: LineContent[];
  areas: AreaContent[];
  onDelete?: (id: string) => void;
  onEdit?: (id: string) => void;
}

const DrawingPopup: React.FC<{
  name: string;
  measurement: string;
  note?: string;
  id: string;
  onDelete?: (id: string) => void;
  onEdit?: (id: string) => void;
}> = ({ name, measurement, note, id, onDelete, onEdit }) => {
  const { t } = useTranslation();
  return (
  <div className="min-w-[140px]">
    <p className="text-[12px] font-bold text-stone-800 mb-0.5">{name}</p>
    <p className="text-2xs text-[var(--text-muted)] mb-1">{measurement}</p>
    {note && <p className="text-2xs text-[var(--text-muted)] mb-2">{note}</p>}
    <div className="flex gap-1">
      {onEdit && (
        <button
          onClick={() => onEdit(id)}
          className="flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-lg text-2xs font-bold text-[var(--text-muted)] hover:bg-stone-200"
        >
          <Edit3 size={10} /> {t("auto.map.drawinglayer.rediger", "Rediger")}
        </button>
      )}
      {onDelete && (
        <button
          onClick={() => onDelete(id)}
          className="flex items-center gap-1 px-2 py-1 bg-red-50 rounded-lg text-2xs font-bold text-red-500 hover:bg-red-100"
        >
          <Trash2 size={10} /> {t("auto.map.drawinglayer.slett", "Slett")}
        </button>
      )}
    </div>
  </div>
  );
};

const DrawingLayer: React.FC<DrawingLayerProps> = ({ lines, areas, onDelete, onEdit }) => {
  const { t } = useTranslation();
  return (
    <>
      {lines.map(line => (
        <Polyline
          key={line.id}
          positions={line.coordinates.map(([lat, lon]) => [lat, lon] as [number, number])}
          pathOptions={{
            color: line.color,
            weight: line.strokeWidth,
            opacity: 0.85,
            dashArray: line.strokeStyle === 'dashed' ? '10,6' : line.strokeStyle === 'dotted' ? '3,6' : undefined,
          }}
        >
          <Popup>
            <DrawingPopup
              name={line.name}
              measurement={formatDistance(line.distance)}
              note={line.note}
              id={line.id}
              onDelete={onDelete}
              onEdit={onEdit}
            />
          </Popup>
        </Polyline>
      ))}

      {areas.map(area => (
        <Polygon
          key={area.id}
          positions={area.coordinates.map(([lat, lon]) => [lat, lon] as [number, number])}
          pathOptions={{
            color: area.color,
            weight: area.strokeWidth,
            fillColor: area.color,
            fillOpacity: area.fillOpacity,
          }}
        >
          <Popup>
            <DrawingPopup
              name={area.name}
              measurement={formatArea(area.areaSqm)}
              note={area.note}
              id={area.id}
              onDelete={onDelete}
              onEdit={onEdit}
            />
          </Popup>
        </Polygon>
      ))}
    </>
  );
};

export default DrawingLayer;
